import { useRef, useState } from 'react';
import { 
  DEFAULT_TABLE_VISIBILITY, 
  DEFAULT_TODOLIST_VISIBILITY 
} from '../config/fieldVisibility';

const DEFAULT_KANBAN_FILTERS = {};

// Default visibility settings for flow view HUD elements
const DEFAULT_FLOW_VISIBILITY = {
  activeMissions: true,
  availableMissions: true,
  milestones: true,
  minimap: true,
  campaignsKey: true,
};

/**
 * useSprintDetailState Hook
 * 
 * Manages all the local state for the SprintDetail component.
 * Extracts state declarations to reduce main component size.
 */
const useSprintDetailState = () => {
  // Refs
  const flowchartRef = useRef(null);
  const todoModalTriggerRef = useRef(null);

  // UI state
  const [isArtifactSummaryOpen, setIsArtifactSummaryOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editingKanbanColumnId, setEditingKanbanColumnId] = useState(null);
  const [detailSidebarTaskId, setDetailSidebarTaskId] = useState(null);
  const [attachmentsModalTaskId, setAttachmentsModalTaskId] = useState(null);
  const [deliverablesModalTaskId, setDeliverablesModalTaskId] = useState(null);
  
  // Campaign picker modal state (for multi-campaign mode)
  const [isCampaignPickerOpen, setIsCampaignPickerOpen] = useState(false);
  // Campaign picker mode: 'add' for adding mission, 'paste' for pasting clipboard data
  const [campaignPickerMode, setCampaignPickerMode] = useState('add');
  // Pending paste data (stored when paste is triggered in multi-campaign mode)
  const pendingPasteDataRef = useRef(null);
  
  // Per-view filters (each view has its own filter state)
  const [filtersPerView, setFiltersPerView] = useState({
    flow: DEFAULT_KANBAN_FILTERS,
    kanban: DEFAULT_KANBAN_FILTERS,
    table: DEFAULT_KANBAN_FILTERS,
    gantt: DEFAULT_KANBAN_FILTERS,
    todolist: DEFAULT_KANBAN_FILTERS,
    neural: DEFAULT_KANBAN_FILTERS,
    board: DEFAULT_KANBAN_FILTERS,
  });
  
  // View state
  const [showArchived, setShowArchived] = useState(false);
  const [showActionableOnly, setShowActionableOnly] = useState(false);
  const [showNestedCampaigns, setShowNestedCampaigns] = useState(true);
  
  // TodoList view state
  // todoViewMode is now derived from todoSwimlane and todoShowNestedItems
  const [todoViewMode, setTodoViewMode] = useState('master');
  const [todoSwimlaneGroupBy, setTodoSwimlaneGroupBy] = useState('status');
  // New: Swimlane setting for todo (same as Kanban - 'none', 'tag', 'priority', etc)
  const [todoSwimlane, setTodoSwimlane] = useState('none');
  // New: Show nested items toggle (replaces Master view mode behavior)
  const [todoShowNestedItems, setTodoShowNestedItems] = useState(true);
  
  // Table swimlane setting (same as Kanban - 'none', 'tag', 'priority', etc)
  // Default is 'none' (no grouping) - previously table always grouped by primary tag
  const [tableSwimlane, setTableSwimlane] = useState('none');

  // Selection state for FOKUS mode (updated via events)
  const [kanbanSelectedIds, setKanbanSelectedIds] = useState(new Set());
  const [tableSelectedIds, setTableSelectedIds] = useState(new Set());
  const [ganttSelectedIds, setGanttSelectedIds] = useState(new Set());
  const [todoSelectedIds, setTodoSelectedIds] = useState(new Set());
  const [neuralSelectedIds, setNeuralSelectedIds] = useState(new Set());
  
  // Table column visibility state
  const [tableColumnVisibility, setTableColumnVisibility] = useState(DEFAULT_TABLE_VISIBILITY);
  
  // Todo element visibility state
  const [todoElementVisibility, setTodoElementVisibility] = useState(DEFAULT_TODOLIST_VISIBILITY);
  
  // Local flow view visibility state (for custom views - overrides prop when set)
  // null means use the prop value, non-null means use this local value
  const [localFlowViewVisibility, setLocalFlowViewVisibility] = useState(null);
  
  // Date validation error modal state
  const [dateValidationError, setDateValidationError] = useState(null);
  
  // Custom fields manager modal state
  const [isCustomFieldsManagerOpen, setIsCustomFieldsManagerOpen] = useState(false);
  
  // Custom field rename modal state (for paste conflicts)
  // { clipboardField, existingField, existingFieldNames, resolve, reject }
  const [renameFieldModal, setRenameFieldModal] = useState(null);
  
  // Deliverable submit modal state
  // { isOpen, taskId, taskName, deliverable }
  const [deliverableSubmitModal, setDeliverableSubmitModal] = useState(null);

  return {
    // Refs
    flowchartRef,
    todoModalTriggerRef,
    
    // UI state
    isArtifactSummaryOpen,
    setIsArtifactSummaryOpen,
    isHistoryOpen,
    setIsHistoryOpen,
    editingKanbanColumnId,
    setEditingKanbanColumnId,
    detailSidebarTaskId,
    setDetailSidebarTaskId,
    attachmentsModalTaskId,
    setAttachmentsModalTaskId,
    deliverablesModalTaskId,
    setDeliverablesModalTaskId,
    isCampaignPickerOpen,
    setIsCampaignPickerOpen,
    campaignPickerMode,
    setCampaignPickerMode,
    pendingPasteDataRef,
    
    // Filter state
    filtersPerView,
    setFiltersPerView,
    
    // View state
    showArchived,
    setShowArchived,
    showActionableOnly,
    setShowActionableOnly,
    showNestedCampaigns,
    setShowNestedCampaigns,
    
    // TodoList state
    todoViewMode,
    setTodoViewMode,
    todoSwimlaneGroupBy,
    setTodoSwimlaneGroupBy,
    todoSwimlane,
    setTodoSwimlane,
    todoShowNestedItems,
    setTodoShowNestedItems,
    
    // Table swimlane state
    tableSwimlane,
    setTableSwimlane,
    
    // Selection state
    kanbanSelectedIds,
    setKanbanSelectedIds,
    tableSelectedIds,
    setTableSelectedIds,
    ganttSelectedIds,
    setGanttSelectedIds,
    todoSelectedIds,
    setTodoSelectedIds,
    neuralSelectedIds,
    setNeuralSelectedIds,
    
    // Table column visibility
    tableColumnVisibility,
    setTableColumnVisibility,
    
    // Todo element visibility
    todoElementVisibility,
    setTodoElementVisibility,
    
    // Local flow view visibility (for custom views)
    localFlowViewVisibility,
    setLocalFlowViewVisibility,
    DEFAULT_FLOW_VISIBILITY,
    
    // Date validation error modal
    dateValidationError,
    setDateValidationError,
    
    // Custom fields manager modal
    isCustomFieldsManagerOpen,
    setIsCustomFieldsManagerOpen,
    
    // Custom field rename modal (for paste conflicts)
    renameFieldModal,
    setRenameFieldModal,
    
    // Deliverable submit modal
    deliverableSubmitModal,
    setDeliverableSubmitModal,
  };
};

export default useSprintDetailState;
